import React, { useState, useEffect } from 'react';
import { X, Menu, Scale, Search, Bell, Settings, Users, Upload, LogOut } from 'lucide-react';
import { CASE_PLAYBOOKS, calcPlaybookDeadline } from '../utils/playbooks';
import { updateProfilePicture } from '../services/userService';
import { toast } from '../utils/toast';
import { isOverdue, daysUntilAccra, accraCalendarDateString, formatAccraDate } from '../utils/firmassistTime';
import { db } from '../firebase-config';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const Header = ({ sidebarOpen, setSidebarOpen, notifications, user, onLogout, firmProfile, setActiveTab, recentCases, upcomingTasks, meetings, userRole, firmId, onNavigateToPersonalNotificationSettings }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // Server-issued in-app notifications addressed to this member (reminder
  // escalations, WhatsApp-verification prompts). Unread ones head the bell.
  const [serverNotes, setServerNotes] = useState([]);
  useEffect(() => {
    if (!user || !user.uid || !firmId) { setServerNotes([]); return undefined; }
    const q = query(collection(db, 'notifications'), where('userId', '==', user.uid), where('firmId', '==', firmId), where('read', '==', false), limit(20));
    return onSnapshot(q, (snap) => setServerNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setServerNotes([]));
  }, [user, firmId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.header-dropdown') && !e.target.closest('.header-icon')) {
        setShowNotifications(false);
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Search across cases and tasks
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    const s = searchTerm.toLowerCase();
    const results = [];

    // Search cases
    (recentCases || []).forEach(c => {
      if (c.name?.toLowerCase().includes(s) || c.caseNumber?.toLowerCase().includes(s) || c.clientName?.toLowerCase().includes(s)) {
        results.push({ type: 'case', id: c.id, title: c.name, subtitle: c.clientName || c.caseNumber, icon: '⚖️' });
      }
    });

    // Search tasks (exclude completed)
    (upcomingTasks || []).forEach(t => {
      if (t.status === 'completed' || t.completed) return;
      if (t.title?.toLowerCase().includes(s) || t.caseName?.toLowerCase().includes(s)) {
        results.push({ type: 'task', id: t.id, title: t.title, subtitle: t.caseName || t.dueDate, icon: '📋' });
      }
    });

    setSearchResults(results.slice(0, 8));
  }, [searchTerm, recentCases, upcomingTasks]);

  // Build notification items from real data
  const buildNotifications = () => {
    const notifs = [];
    // Africa/Accra business-time semantics throughout (utils/firmassistTime.js)
    // — a viewer's own device timezone must never change which items read
    // as overdue/due-today/due-tomorrow/etc.
    const todayStr = accraCalendarDateString();

    (upcomingTasks || []).forEach(t => {
      if (t.status === 'completed') return;
      const isCourt = t.eventType === 'court_date';
      const daysUntil = daysUntilAccra(t.dueDate);
      if (daysUntil === null) return;

      // Overdue
      if (isOverdue(t)) {
        notifs.push({ id: 'overdue-' + t.id, type: 'overdue', title: (isCourt ? '⚖️ OVERDUE COURT DATE: ' : 'Overdue: ') + t.title, subtitle: (t.caseName || '') + ' — was due ' + formatAccraDate(t.dueDate, { month: 'short', day: 'numeric' }, 'en-US'), color: '#ef4444', icon: '🔴', priority: 0, taskId: t.id, caseId: t.caseId, caseName: t.caseName, navTab: 'tasks' });
      }
      // Due today
      else if (t.dueDate === todayStr) {
        notifs.push({ id: 'today-' + t.id, type: 'due_today', title: (isCourt ? '⚖️ COURT TODAY: ' : 'Due today: ') + t.title, subtitle: (t.caseName || '') + (t.dueTime ? ' at ' + t.dueTime : ''), color: isCourt ? '#ef4444' : '#f59e0b', icon: isCourt ? '⚖️' : '🟡', priority: 1, taskId: t.id, caseId: t.caseId, caseName: t.caseName, navTab: 'tasks' });
      }
      // Due tomorrow
      else if (daysUntil === 1) {
        notifs.push({ id: 'tomorrow-' + t.id, type: 'due_tomorrow', title: (isCourt ? '⚖️ Court tomorrow: ' : 'Due tomorrow: ') + t.title, subtitle: (t.caseName || '') + (t.dueTime ? ' at ' + t.dueTime : ''), color: isCourt ? '#ef4444' : '#f59e0b', icon: isCourt ? '⚖️' : '🟠', priority: 2, taskId: t.id, caseId: t.caseId, caseName: t.caseName, navTab: 'tasks' });
      }
      // Due in 3 days
      else if (daysUntil > 1 && daysUntil <= 3) {
        notifs.push({ id: '3day-' + t.id, type: 'due_3days', title: (isCourt ? '⚖️ Court in ' + daysUntil + ' days: ' : 'Due in ' + daysUntil + ' days: ') + t.title, subtitle: t.caseName || '', color: '#3b82f6', icon: isCourt ? '⚖️' : '🔵', priority: 3, taskId: t.id, caseId: t.caseId, caseName: t.caseName, navTab: 'tasks' });
      }
      // Due in 7 days (only court dates and high priority)
      else if (daysUntil > 3 && daysUntil <= 7 && (isCourt || t.priority === 'high')) {
        notifs.push({ id: '7day-' + t.id, type: 'due_7days', title: (isCourt ? '⚖️ Court in ' + daysUntil + ' days: ' : daysUntil + ' days: ') + t.title, subtitle: t.caseName || '', color: '#d4a853', icon: isCourt ? '⚖️' : '📅', priority: 4, taskId: t.id, caseId: t.caseId, caseName: t.caseName, navTab: 'tasks' });
      }
    });

    // Case deadlines
    (recentCases || []).forEach(c => {
      if (c.status === 'Closed' || c.status === 'closed' || !c.deadline) return;
      const daysUntil = daysUntilAccra(c.deadline);
      if (daysUntil === null) return;

      if (daysUntil < 0) {
        notifs.push({ id: 'case-overdue-' + c.id, type: 'overdue', title: '🔴 Case deadline passed: ' + c.name, subtitle: c.client || '', color: '#ef4444', icon: '🔴', priority: 0, caseId: c.id, caseName: c.name, navTab: 'cases' });
      } else if (daysUntil === 0) {
        notifs.push({ id: 'case-today-' + c.id, type: 'due_today', title: '⚠️ Case deadline TODAY: ' + c.name, subtitle: c.client || '', color: '#ef4444', icon: '⚠️', priority: 1, caseId: c.id, caseName: c.name, navTab: 'cases' });
      } else if (daysUntil <= 3) {
        notifs.push({ id: 'case-3day-' + c.id, type: 'due_3days', title: 'Case deadline in ' + daysUntil + ' day' + (daysUntil !== 1 ? 's' : '') + ': ' + c.name, subtitle: c.client || '', color: '#f59e0b', icon: '📁', priority: 2, caseId: c.id, caseName: c.name, navTab: 'cases' });
      } else if (daysUntil <= 7) {
        notifs.push({ id: 'case-7day-' + c.id, type: 'due_7days', title: 'Case deadline in ' + daysUntil + ' days: ' + c.name, subtitle: c.client || '', color: '#d4a853', icon: '📁', priority: 4, caseId: c.id, caseName: c.name, navTab: 'cases' });
      }
    });

    // Team announcements (last 24 hours)

    // ── PLAYBOOK DEADLINE NOTIFICATIONS ──
    (recentCases || []).forEach(c => {
      if (c.status === 'Closed' || c.status === 'closed') return;
      const pbKey = c.casePlaybook;
      const pbDef = pbKey && CASE_PLAYBOOKS[pbKey];
      if (!pbDef) return;
      const keyDates = c.playbookDates || {};
      const completedSteps = c.playbookCompleted || [];
      
      Object.values(pbDef.stages).forEach(stage => {
        stage.steps.forEach(step => {
          if (completedSteps.includes(step.id)) return; // already done
          if (!step.deadlineFrom || !keyDates[step.deadlineFrom]) return; // no deadline calculable
          const deadline = calcPlaybookDeadline(keyDates, step.deadlineFrom, step.deadlineDays);
          if (!deadline) return;
          const daysUntil = daysUntilAccra(deadline);
          if (daysUntil === null) return;

          if (daysUntil < 0) {
            notifs.push({ id: 'pb-overdue-' + c.id + '-' + step.id, type: 'overdue', title: '📋 OVERDUE: ' + step.label, subtitle: c.name + ' — ' + Math.abs(daysUntil) + ' day' + (Math.abs(daysUntil) !== 1 ? 's' : '') + ' overdue | ' + step.rule, color: '#ef4444', icon: '📋', priority: 0, caseId: c.id, caseName: c.name, navTab: 'cases', isPlaybook: true });
          } else if (daysUntil === 0) {
            notifs.push({ id: 'pb-today-' + c.id + '-' + step.id, type: 'due_today', title: '📋 DUE TODAY: ' + step.label, subtitle: c.name + ' | ' + step.rule, color: '#ef4444', icon: '📋', priority: 1, caseId: c.id, caseName: c.name, navTab: 'cases', isPlaybook: true });
          } else if (daysUntil <= 3) {
            notifs.push({ id: 'pb-3day-' + c.id + '-' + step.id, type: 'due_3days', title: '📋 ' + daysUntil + ' day' + (daysUntil !== 1 ? 's' : '') + ': ' + step.label, subtitle: c.name + ' | ' + step.rule, color: '#f59e0b', icon: '📋', priority: 2, caseId: c.id, caseName: c.name, navTab: 'cases', isPlaybook: true });
          }
        });
      });
    });

    if (meetings) {
      (meetings || []).forEach(m => {
        const daysUntil = daysUntilAccra(m.meetingDate); // accepts a Firestore Timestamp or date string — see utils/firmassistTime.js
        if (daysUntil === null) return;
        if (daysUntil === 0) {
          notifs.push({ id: 'meeting-today-' + m.id, type: 'due_today', title: '📅 Meeting today: ' + (m.title || m.clientName || 'Meeting'), subtitle: m.meetingTime ? 'at ' + m.meetingTime : '', color: '#d4a853', icon: '📅', priority: 1, meetingId: m.id, navTab: 'tasks' });
        } else if (daysUntil === 1) {
          notifs.push({ id: 'meeting-tmrw-' + m.id, type: 'due_tomorrow', title: '📅 Meeting tomorrow: ' + (m.title || m.clientName || 'Meeting'), subtitle: m.meetingTime ? 'at ' + m.meetingTime : '', color: '#3b82f6', icon: '📅', priority: 2, meetingId: m.id, navTab: 'tasks' });
        }
      });
    }
    // Sort by priority (most urgent first)
    notifs.sort((a, b) => a.priority - b.priority);
    
    // Browser push for urgent items
    if (notifs.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
      const urgent = notifs.filter(n => n.type === 'overdue' || n.type === 'due_today');
      if (urgent.length > 0 && !window._notifiedThisSession) {
        window._notifiedThisSession = true;
        new Notification('FirmAssist — ' + urgent.length + ' urgent item' + (urgent.length > 1 ? 's' : ''), {
          body: urgent.slice(0, 3).map(n => n.title).join('\n'),
          icon: '/icons/icon-192.png',
          tag: 'firmassist-urgent'
        });
      }
    }

    return notifs.slice(0, 15);
  };

 const serverItems = serverNotes
    .slice()
    .sort((a, b) => ((b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : 0) - (a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : 0)))
    .map((x) => ({
      id: 'server-' + x.id, serverId: x.id, type: x.type === 'reminder_escalation' ? 'overdue' : 'server',
      title: (x.type === 'reminder_escalation' ? '⚠️ ' : '📲 ') + (x.title || 'Notification'), subtitle: x.body || '',
      color: x.type === 'reminder_escalation' ? '#ef4444' : '#d4a853', icon: x.type === 'reminder_escalation' ? '⚠️' : '📲', priority: -1,
      navTab: x.type === 'verify_whatsapp' ? 'verify_whatsapp' : 'tasks',
    }));
  const notificationItems = [...serverItems, ...buildNotifications()];
  const markServerRead = (id) => {
    setServerNotes((prev) => prev.filter((x) => x.id !== id));
    httpsCallable(getFunctions(), 'markNotificationRead')({ ids: [id] }).catch(() => {});
  };
  const overdueCount = notificationItems.filter(n => n.type === 'overdue').length;
  const todayCount = notificationItems.filter(n => n.type === 'due_today').length;
  const tomorrowCount = notificationItems.filter(n => n.type === 'due_tomorrow').length;
  const totalAlerts = overdueCount + todayCount + tomorrowCount + serverItems.filter(n => n.type === 'server').length;

  const handleSearchResultClick = (result) => {
    if (result.type === 'case') {
      setActiveTab('cases');
    } else if (result.type === 'task') {
      setActiveTab('tasks');
    }
    setShowSearch(false);
    setSearchTerm('');
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    background: '#0f1219',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    zIndex: 1000,
    minWidth: '320px',
    maxHeight: '420px',
    overflowY: 'auto'
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="logo">
          <Scale size={32} />
          <div className="logo-text">
            <span className="logo-name">FIRMASSIST</span>
            <span className="logo-tagline">{firmProfile?.name || 'by Tomken Technologies'}</span>
          </div>
        </div>
      </div>

      <div className="header-right" style={{ position: 'relative' }}>
        {/* ─── SEARCH ─── */}
        {showSearch ? (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search cases, tasks..."
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(212,168,83,0.4)',
                borderRadius: '8px',
                padding: '0.45rem 0.75rem 0.45rem 2rem',
                color: '#e8eaed',
                fontSize: '0.85rem',
                outline: 'none',
                width: '240px',
                fontFamily: 'inherit'
              }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setShowSearch(false); setSearchTerm(''); } }}
            />
            <Search size={14} style={{ position: 'absolute', left: '0.6rem', color: '#d4a853' }} />
            <button className="header-icon" onClick={() => { setShowSearch(false); setSearchTerm(''); }} style={{ marginLeft: '0.25rem' }}>
              <X size={16} />
            </button>
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="header-dropdown" style={{ ...dropdownStyle, left: 0, right: 'auto', width: '300px' }}>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#8b8fa3', fontSize: '0.75rem', fontWeight: 600 }}>
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                </div>
                {searchResults.map(r => (
                  <div
                    key={r.id}
                    onClick={() => handleSearchResultClick(r)}
                    style={{
                      padding: '0.75rem 1rem',
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '1.1rem' }}>{r.icon}</span>
                    <div>
                      <div style={{ color: '#e8eaed', fontSize: '0.85rem', fontWeight: 500 }}>{r.title}</div>
                      <div style={{ color: '#8b8fa3', fontSize: '0.75rem' }}>{r.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button className="header-icon" onClick={() => setShowSearch(true)} title="Search">
            <Search size={20} />
          </button>
        )}

        {/* ─── NOTIFICATIONS ─── */}
        <div style={{ position: 'relative' }}>
          <button className="header-icon notification-icon" onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowProfileMenu(false); }}>
            <Bell size={20} />
            {totalAlerts > 0 && <span className="notification-badge">{totalAlerts}</span>}
          </button>
          {showNotifications && (
            <div className="header-dropdown" style={dropdownStyle}>
              <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.95rem' }}>Notifications</span>
                {overdueCount > 0 && (
                  <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600 }}>
                    {overdueCount} overdue
                  </span>
                )}
              </div>
              {notificationItems.length === 0 ? (
                <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#8b8fa3' }}>
                  <Bell size={32} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>All caught up! No pending items.</p>
                </div>
              ) : (
                notificationItems.map(n => (
                  <div
                    key={n.id}
                    onClick={() => {
                      if (n.serverId) markServerRead(n.serverId);
                      if (n.navTab === 'verify_whatsapp') {
                        if (onNavigateToPersonalNotificationSettings) onNavigateToPersonalNotificationSettings('whatsapp');
                      } else if (n.navTab === 'cases' && n.caseId) {
                        setActiveTab('cases');
                        // Use setTimeout to let tab render, then trigger case selection
                        setTimeout(() => {
                          const event = new CustomEvent('firmassist-select-case', { detail: { caseId: n.caseId, caseName: n.caseName, detailsTab: n.isPlaybook ? 'playbook' : 'overview' } });
                          window.dispatchEvent(event);
                        }, 100);
                      } else {
                        setActiveTab(n.navTab || 'tasks');
                      }
                      setShowNotifications(false);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '0.9rem', marginTop: '0.1rem' }}>{n.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#e8eaed', fontSize: '0.85rem', fontWeight: 500 }}>{n.title}</div>
                      {n.subtitle && <div style={{ color: '#8b8fa3', fontSize: '0.75rem', marginTop: '0.15rem' }}>{n.subtitle}</div>}
                    </div>
                  </div>
                ))
              )}
              <div
                onClick={() => { setActiveTab('tasks'); setShowNotifications(false); }}
                style={{
                  padding: '0.75rem 1rem', textAlign: 'center',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  color: '#d4a853', fontSize: '0.85rem', fontWeight: 600,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212,168,83,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                View All Tasks →
              </div>
            </div>
          )}
        </div>

        {/* ─── SETTINGS / PROFILE ─── */}
        <div style={{ position: 'relative' }}>
          <button className="header-icon" onClick={(e) => { e.stopPropagation(); setShowProfileMenu(!showProfileMenu); setShowNotifications(false); }} title="Settings">
            <Settings size={20} />
          </button>
          {showProfileMenu && (
            <div className="header-dropdown" style={{ ...dropdownStyle, minWidth: '260px' }}>
              {/* User Info */}
              <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ position: 'relative' }}>
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #d4a853 0%, #b8862d 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 700
                      }}>
                        {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.9rem' }}>{user?.displayName || 'User'}</div>
                    <div style={{ color: '#8b8fa3', fontSize: '0.75rem' }}>{user?.email}</div>
                    <div style={{
                      display: 'inline-block', marginTop: '0.25rem',
                      background: 'rgba(212,168,83,0.15)', color: '#d4a853',
                      padding: '0.1rem 0.5rem', borderRadius: '10px',
                      fontSize: '0.65rem', fontWeight: 600, textTransform: 'capitalize'
                    }}>
                      {user?.role || 'User'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Firm Info */}
              {firmProfile && (
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ color: '#8b8fa3', fontSize: '0.7rem', marginBottom: '0.25rem' }}>FIRM</div>
                  <div style={{ color: '#e8eaed', fontSize: '0.85rem' }}>{firmProfile.name}</div>
                  {firmProfile.email && <div style={{ color: '#8b8fa3', fontSize: '0.75rem' }}>{firmProfile.email}</div>}
                </div>
              )}

              {/* Menu Items */}
              {userRole === 'admin' && (
                <div
                  onClick={() => { setActiveTab('admin'); setShowProfileMenu(false); }}
                  style={{
                    padding: '0.75rem 1rem',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    cursor: 'pointer', color: '#e8eaed', fontSize: '0.85rem',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Settings size={16} style={{ color: '#8b8fa3' }} />
                  Admin Panel
                </div>
              )}
              {userRole === 'admin' && (
                <div
                  onClick={() => { setActiveTab('admin'); setShowProfileMenu(false); }}
                  style={{
                    padding: '0.75rem 1rem',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    cursor: 'pointer', color: '#e8eaed', fontSize: '0.85rem',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Users size={16} style={{ color: '#8b8fa3' }} />
                  Team Management
                </div>
              )}

              {/* Change Photo */}
              <label
                style={{
                  padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  cursor: 'pointer', color: '#e8eaed', fontSize: '0.85rem',
                  borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Upload size={16} style={{ color: '#8b8fa3' }} /> Change Photo
                <input type="file" accept="image/*" hidden onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const result = await updateProfilePicture(user.uid, file);
                  if (result.success) {
                    window.location.reload();
                  } else {
                    toast(result.error || 'Upload failed', 'error');
                  }
                }} />
              </label>
              {/* Logout */}
              <div
                onClick={onLogout}
                style={{
                  padding: '0.75rem 1rem',
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <LogOut size={16} />
                Sign Out
              </div>
            </div>
          )}
        </div>

        {/* ─── USER AVATAR ─── */}
        {user?.photoURL ? (
          <img src={user.photoURL} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }} title={user?.email} />
        ) : (
          <div className="user-avatar" title={user?.email}>{user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}</div>
        )}
      </div>
    </header>
  );
};

export default Header;
